// SMART_NOTIFICATIONS — WEB-1 toast helper + WEB-4 audio bridge.
//
// Thin façade over Sonner so callers do `notify.fromServer(dto)` instead
// of mapping severity → sonner method themselves. Also routes the event
// through the WEB-4 audio synth and unlocks the audio context lazily.
import { toast } from 'sonner'
import { playSound, unlockAudio } from './sound'

export type Severity = 'info' | 'success' | 'warning' | 'critical'

export interface NotificationDTO {
  id: string
  event_type: string
  severity: Severity
  title_key: string
  body_key: string
  body_params: Record<string, unknown>
  href?: string | null
  read_at?: string | null
  created_at: string
}

interface ShowOpts {
  // Sound is on by default — pass false to mute (e.g. background tab or
  // user opted out in preferences).
  soundEnabled?: boolean
  volumePct?: number
  // Translator — usually `t` from react-i18next. Falls back to title_key.
  t?: (key: string, params?: Record<string, unknown>) => string
  // Action label + click handler (typically navigate to dto.href).
  action?: { label: string; onClick: () => void }
}

const VARIANT: Record<Severity, (msg: string, opts?: Parameters<typeof toast>[1]) => void> = {
  info: (m, o) => toast.info(m, o),
  success: (m, o) => toast.success(m, o),
  warning: (m, o) => toast.warning(m, o),
  critical: (m, o) => toast.error(m, o),
}

export function showNotification(n: NotificationDTO, opts: ShowOpts = {}): void {
  const tr = opts.t ?? ((k) => k)
  const title = tr(n.title_key, n.body_params)
  const body = tr(n.body_key, n.body_params)
  const show = VARIANT[n.severity] ?? VARIANT.info
  show(title, {
    description: body,
    duration: n.severity === 'critical' ? Infinity : undefined,
    action: opts.action,
  })
  playSound(n.severity, {
    muted: opts.soundEnabled === false,
    volumePct: opts.volumePct,
  })
}

// Re-export for convenience so consumers don't import sound directly.
export { unlockAudio }

// Direct re-export for app code that wants ad-hoc toasts (save success,
// validation errors…) without going through the notification pipeline.
export { toast }
